import { observer } from 'mobx-react-lite'
import { useEffect, useState } from 'react'

import AiAgentIcon from '@src/app/Automations/AiAgentIcon/AiAgentIcon'
import { useAppStore } from '@src/app/context'
import OngoingCallDialogHeader from '@src/app/voice/ongoing-call/components/OngoingCallDialogHeader'
import Timer from '@src/component/timer'
import { logError } from '@src/lib/log'
import type Service from '@src/service'
import aiAgentConstants from '@src/service/aiAgentConstants'
import type { ActiveCall } from '@src/service/voice'
import Button from '@ui/Button/Button'
import { LoadingIndicator } from '@ui/LoadingIndicator'
import Typography from '@ui/Typography'
import { HStack, VStack } from '@ui/layout/Stack'

import * as styles from './VoiceAgentCallDialog.css'
import OngoingCallDialog from './ongoing-call/components/OngoingCallDialog'
import OngoingCallDialogPanel from './ongoing-call/components/OngoingCallDialogPanel'
import OngoingCallWaveform from './ongoing-call/components/OngoingCallWaveform'

interface VoiceAgentCallDialogProps {
  call: ActiveCall
}

async function deleteWorkflowDefinitionId(call: ActiveCall, service: Service) {
  const workflowDefinitionId = call.voiceAgentTest?.workflowDefinitionId
  if (!workflowDefinitionId) {
    throw new Error('Missing workflow definition ID or agent definition ID')
  }
  try {
    await service.workflow.deleteWorkflowDefinition(workflowDefinitionId)
  } catch (error) {
    logError(error, { message: 'Error deleting workflow definition' })
  }
}

function VoiceAgentCallDialog({ call }: VoiceAgentCallDialogProps) {
  const duration = call.startedAt?.getTime()
  const [analyser, setAnalyser] = useState<AnalyserNode | null>(null)
  const [isActive, setIsActive] = useState<boolean>(false)
  const { service } = useAppStore()

  useEffect(() => {
    const setupAnalyserNode = () => {
      try {
        if (call.remoteAnalyserNode) {
          setAnalyser(call.remoteAnalyserNode)
          setIsActive(true)
        }
      } catch (error) {
        logError(error, { message: 'Error setting up analyser node' })
      }
    }

    setupAnalyserNode()
  }, [call.remoteAnalyserNode])

  useEffect(() => {
    if (!isActive) {
      return
    }
    // delete the workflowDefinition once the call is active
    void deleteWorkflowDefinitionId(call, service)
  }, [isActive])

  const handleEndTestCall = async () => {
    try {
      await call.endForEveryone()
    } catch (error) {
      logError(error, { message: 'Error ending test call' })
    }
  }

  return (
    <OngoingCallDialog>
      <OngoingCallDialogPanel>
        <OngoingCallDialogHeader>
          <HStack gap={8}>
            <AiAgentIcon className={styles.aiAigentIcon} />
            <Typography variant="caption1" color="textSecondary" fontWeight="medium">
              Testing {aiAgentConstants.name}
            </Typography>
            {duration && (
              <Timer
                variant="caption1"
                color="textTertiary"
                fontWeight="regular"
                startTimestamp={duration}
              />
            )}
          </HStack>
        </OngoingCallDialogHeader>
        {!analyser ? (
          <VStack className={styles.fixedDimensions} justifyContent="center">
            <LoadingIndicator
              color="gray"
              variant="circular"
              aria-label="Loading test call"
            />
          </VStack>
        ) : (
          <VStack className={styles.fixedDimensions} justifyContent="center">
            <OngoingCallWaveform
              analyserNode={analyser}
              isActive={isActive}
              width={304}
              barWidth={3}
              barGap={6}
              minBarHeight={2}
              maxBars={32}
            />
          </VStack>
        )}
      </OngoingCallDialogPanel>
      <Button variant="pastel" size="sm" onClick={handleEndTestCall}>
        <Typography variant="footnote" fontWeight="medium">
          End test
        </Typography>
      </Button>
    </OngoingCallDialog>
  )
}

export default observer(VoiceAgentCallDialog)
